import React, { useState } from "react";
import { ExternalLink, Wand2, RefreshCw, Radar, Dna, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import CTAControlPanel from "../components/CTAControlPanel";
import PostCard from "../components/PostCard";
import SkeletonPost from "../components/SkeletonPost";
import PromoteBatchVideoGenerator from "../components/PromoteBatchVideoGenerator";
import PromoteVideoUploads from "../components/PromoteVideoUploads";
import CopyCatPanel from "../components/CopyCatPanel";
import StaticAdPanel from "../components/StaticAdPanel";
import ViralScoreCard from "../components/ViralScoreCard";
import { chatJSON } from "../lib/openai";
import { buildAffiliateBRollPrompt, buildViralScorePrompt, enforceCtaInAllPosts } from "../lib/prompts";
import { gatherSignalsForPartner } from "../lib/trends";
import { toast } from "sonner";

const AFFILIATE_URL = "https://the-nomadic-wea.samcart.com/affiliates/login";

export const PartnershipPortal = ({ apiKey }) => {
  // Affiliate / partner tab: CTA keyword defaults to GO, Auto CTA on by default.
  const [ctaText, setCtaText] = useState("GO");
  const [offer, setOffer] = useState("The Nomadic Wealth Academy");
  const [autoCTA, setAutoCTA] = useState(true);

  const [stage, setStage] = useState("idle"); // idle | trends | generating | scoring | retrying
  const [posts, setPosts] = useState(null);
  const [signals, setSignals] = useState(null);
  const [scoreBundle, setScoreBundle] = useState(null); // {scores, batchOverall, attempts}

  // Generation strategy:
  //   1. Fetch live trend signals (Reddit + Google Trends + YouTube).
  //   2. Ask OpenAI for 4 reels. Score them on 5 dimensions.
  //   3. If batch_overall < 85, regenerate up to MAX_ATTEMPTS times and keep
  //      the batch with the highest batch_overall. (User-requested viral
  //      auto-regenerate loop — capped at 3 attempts.)
  const MAX_ATTEMPTS = 3;
  const TARGET_SCORE = 85;

  const generateOnce = async ({ apiKey, system, user, trendsDigest }) => {
    setStage("generating");
    const res = await chatJSON({ apiKey, system, user, temperature: 1.0 });
    if (!res.posts || !Array.isArray(res.posts)) throw new Error("Response missing posts");
    const enforced = enforceCtaInAllPosts(res.posts);

    // Score the batch — wrapped in try so a scoring failure never blocks
    // surfacing the actual posts (we'd rather ship a batch than refuse).
    let score = null;
    try {
      setStage("scoring");
      const scorePrompt = buildViralScorePrompt({ posts: enforced, trendsDigest });
      score = await chatJSON({
        apiKey,
        system: scorePrompt.system,
        user: scorePrompt.user,
        temperature: 0.2,
      });
    } catch (e) {
      console.warn("[partnership] viral scoring failed:", e?.message || e);
    }
    return { posts: enforced, score };
  };

  const generate = async () => {
    try {
      setStage("trends");
      const sigs = await gatherSignalsForPartner();
      setSignals(sigs);

      // Categorise which live sources actually returned data — exposed in the UI
      // as a persistent chip so the user always knows what the engine was fed.
      const sourcesHit = {
        reddit:  (sigs.sources?.reddit_count  || 0) > 0,
        youtube: (sigs.sources?.youtube_count || 0) > 0,
        google:  (sigs.sources?.google_count  || 0) > 0,
      };
      const totalHits = (sigs.sources?.reddit_count || 0) + (sigs.sources?.youtube_count || 0) + (sigs.sources?.google_count || 0);
      if (totalHits === 0 && sigs.cache_used) {
        toast.warning(`Live trend sources all failed — using cached signals from ~${sigs.sources?.cache_age_minutes ?? "?"} min ago.`);
      } else if (totalHits === 0) {
        toast.warning("Live trend sources all failed — falling back to the Audience DNA + Hook frameworks.");
      } else {
        toast.message(
          `Pulled ${sigs.sources.reddit_count} Reddit · ${sigs.sources.youtube_count} YouTube · ${sigs.sources.google_count} Google Trends`
        );
      }

      setStage("generating");
      const { system, user } = buildAffiliateBRollPrompt({
        ctaText,
        offer,
        autoCTA,
        trendsDigest: sigs.digest,
      });

      // Run up to MAX_ATTEMPTS — keep whichever batch scored highest.
      let best = null;
      let attempts = 0;
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        attempts = i + 1;
        if (i > 0) {
          setStage("retrying");
          toast.message(`Score below ${TARGET_SCORE} — regenerating (attempt ${attempts}/${MAX_ATTEMPTS})…`);
        }
        const candidate = await generateOnce({ apiKey, system, user, trendsDigest: sigs.digest });
        const overall = Number(candidate?.score?.batch_overall) || 0;
        if (!best || overall > (Number(best.score?.batch_overall) || 0)) {
          best = { ...candidate, attempts };
        }
        if (overall >= TARGET_SCORE) break;
      }

      if (!best) throw new Error("Generation failed");
      setPosts(best.posts);
      if (best.score?.scores) {
        setScoreBundle({
          scores: best.score.scores,
          batchOverall: Number(best.score.batch_overall) || 0,
          attempts: best.attempts,
        });
      } else {
        setScoreBundle(null);
      }
      const finalScore = Number(best.score?.batch_overall) || 0;
      if (finalScore >= TARGET_SCORE) {
        toast.success(`4 posts ready · viral score ${finalScore}/100`);
      } else if (finalScore > 0) {
        toast.success(`4 posts ready · best of ${best.attempts} tries (${finalScore}/100)`);
      } else {
        toast.success("4 affiliate posts ready — grounded in live trends");
      }
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setStage("idle");
    }
  };

  const updatePost = (i, updated) => {
    setPosts((prev) => prev.map((p, idx) => (idx === i ? updated : p)));
  };

  const loading = stage !== "idle";
  const loadingLabel =
    stage === "trends" ? "Scanning Reddit + YouTube + Google Trends…"
      : stage === "generating" ? "Writing posts…"
      : stage === "scoring" ? "Scoring virality…"
      : stage === "retrying" ? "Auto-regenerating for a higher score…"
      : "";

  return (
    <div className="space-y-10">
      <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-zinc-950 p-8 sm:p-12">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative">
          <div className="inline-flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              Nomadic Wealth Academy · Partner
            </div>

            {/* Audience-DNA confirmation badge — purely informational. Hovers
                reveal the baked-in audience segments + emotional drivers
                so the user knows every Partner-tab generation is already
                grounded in the Nomadic Wealth Academy buyer psychology. */}
            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid="active-audience-badge"
                    aria-label="Active audience DNA — Nomadic Wealth Academy"
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-colors cursor-help"
                  >
                    <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />
                    Active audience
                    <span className="hidden xs:inline text-emerald-200/80 font-medium normal-case tracking-normal">
                      · Nomadic Wealth Academy
                    </span>
                    <Dna className="h-3 w-3 ml-0.5 opacity-80" strokeWidth={2.2} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  sideOffset={10}
                  className="max-w-sm bg-zinc-950 border border-emerald-500/30 text-zinc-200 p-4 shadow-[0_20px_60px_-15px_rgba(16,185,129,0.45)]"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300 mb-2">
                    DNA baked into every post
                  </div>
                  <div className="space-y-2 text-[12.5px] leading-relaxed">
                    <div>
                      <div className="text-zinc-400 text-[11px] uppercase tracking-wider font-semibold mb-0.5">
                        Audience segments
                      </div>
                      Aspiring digital nomads (22–38) · Remote workers seeking freedom (25–45) · AI / online business seekers (18–40) · Escape + reinvention (24–50)
                    </div>
                    <div>
                      <div className="text-zinc-400 text-[11px] uppercase tracking-wider font-semibold mb-0.5">
                        Emotional drivers
                      </div>
                      Burnout · feeling trapped · cost-of-living pressure · corporate dissatisfaction · desire for adventure · time freedom · AI leverage
                    </div>
                    <div className="pt-1.5 border-t border-white/10 text-[11px] text-zinc-500">
                      Reddit ecosystem · creator references · search intent · language patterns · offer positioning — all pre-grounded.
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white max-w-3xl leading-[1.05]">
            Promote The Nomadic Wealth Academy & <span className="text-amber-400">Earn 100% Profit</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-zinc-400 leading-relaxed">
            Drive traffic. Keep 100% of the commission. Every post is grounded in live Reddit + Google Trends + YouTube data — so your hooks hit what people are actually searching for today.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={AFFILIATE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="access-affiliate-portal-link"
            >
              <Button className="h-12 rounded-full bg-amber-500 text-[#1c1302] font-semibold hover:bg-amber-400 px-6 transition-all duration-200">
                <ExternalLink className="mr-2 h-4 w-4" strokeWidth={2} />
                Access Affiliate Portal
              </Button>
            </a>
            <a
              href="https://thenomadicwealthacademy.com/partner"
              target="_blank"
              rel="noreferrer"
              data-testid="affiliate-signup-link"
            >
              <Button
                variant="ghost"
                className="h-12 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:text-emerald-100 hover:border-emerald-400/70 font-semibold px-5 transition-all duration-200"
              >
                Sign Up
              </Button>
            </a>
            <div className="inline-flex items-center gap-4 rounded-full border border-white/10 bg-zinc-950/60 px-5 h-12 text-xs text-zinc-400">
              <span><span className="text-amber-400 font-semibold">100%</span> commissions</span>
              <span className="h-4 w-px bg-white/10" />
              <span>Global payouts</span>
              <span className="h-4 w-px bg-white/10 hidden sm:inline-block" />
              <span className="hidden sm:inline">No cap</span>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start space-y-4">
          <PromoteVideoUploads />
          <CTAControlPanel
            ctaText={ctaText}
            setCtaText={setCtaText}
            offer={offer}
            setOffer={setOffer}
            autoCTA={autoCTA}
            setAutoCTA={setAutoCTA}
          />
          <Button
            onClick={generate}
            disabled={loading}
            data-testid="generate-affiliate-button"
            className="w-full h-12 rounded-full bg-amber-500 text-[#1c1302] font-semibold hover:bg-amber-400 disabled:opacity-60"
          >
            {loading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" strokeWidth={2} />
                {loadingLabel}
              </>
            ) : (
              <>
                <Wand2 className="mr-2 h-4 w-4" strokeWidth={2} />
                {posts ? "GENERATE CONTENT" : "GENERATE CONTENT"}
              </>
            )}
          </Button>
          {signals && (
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-[11px] text-emerald-200/90 flex items-center gap-2">
              <Radar className="h-3.5 w-3.5" strokeWidth={1.8} />
              {(signals.sources.reddit_count || signals.sources.google_count || signals.sources.youtube_count) ? (
                <>
                  Grounded in {signals.sources.reddit_count} Reddit posts
                  {signals.sources.google_count ? ` · ${signals.sources.google_count} Google trends` : ""}
                </>
              ) : (
                <>Grounded in Market Research · Hook Library · Knowledge Base</>
              )}
            </div>
          )}
        </aside>

        <div className="lg:col-span-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold text-white">
              Your 4-Post Daily Drop
            </h2>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
              Educate · Entertain · Inspire · Sell
            </span>
          </div>

          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[0, 1, 2, 3].map((i) => (
                <SkeletonPost key={i} />
              ))}
            </div>
          )}

          {!loading && !posts && (
            <div className="rounded-2xl border border-dashed border-white/10 bg-zinc-900/40 p-10 text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30">
                <Wand2 className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <p className="mt-4 font-display text-lg text-white">
                Configure your CTA, then hit Generate.
              </p>
              <p className="mt-1 text-sm text-zinc-500">
                We&apos;ll pull live trending data from Reddit + Google Trends + YouTube and ground every post in what people are actually searching for.
              </p>
            </div>
          )}

          {!loading && posts && (
            <>
              {signals?.sources && (
                <div
                  data-testid="trend-sources-chip-row"
                  className="mb-4 flex flex-wrap items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em]"
                >
                  <span className="text-zinc-500">Grounded in:</span>
                  {(signals.sources.reddit_count || signals.sources.youtube_count || signals.sources.google_count)
                    ? [
                        { key: "reddit",  label: "Reddit",        count: signals.sources.reddit_count  || 0 },
                        { key: "youtube", label: "YouTube",       count: signals.sources.youtube_count || 0 },
                        { key: "google",  label: "Google Trends", count: signals.sources.google_count  || 0 },
                      ].map((s) => {
                    const ok = s.count > 0;
                    return (
                      <span
                        key={s.key}
                        data-testid={`trend-source-${s.key}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${
                          ok
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                            : "border-zinc-700/50 bg-zinc-900/40 text-zinc-500 line-through"
                        }`}
                        title={ok ? `${s.count} signals from ${s.label}` : `${s.label} unreachable — fell back to intelligence layer`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-zinc-600"}`} />
                        {s.label} · {s.count}
                      </span>
                    );
                  })
                  : ["Market Research", "Hook Library", "Knowledge Base"].map((label) => (
                      <span
                        key={label}
                        data-testid={`trend-source-fallback-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-200"
                        title="Live trends unavailable — falling back to uploaded intelligence layer"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        {label}
                      </span>
                    ))
                  }
                </div>
              )}
              {scoreBundle?.scores?.length ? (
                <ViralScoreCard
                  scores={scoreBundle.scores}
                  batchOverall={scoreBundle.batchOverall}
                  attempts={scoreBundle.attempts}
                />
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {posts.map((p, i) => (
                  <PostCard
                    key={i}
                    post={p}
                    index={i}
                    apiKey={apiKey}
                    onChange={(updated) => updatePost(i, updated)}
                  />
                ))}
              </div>
              <StaticAdPanel
                apiKey={apiKey}
                niche="digital nomad / living abroad"
                audience="Americans curious about moving abroad or earning remotely"
                offer="The Nomadic Wealth Academy"
              />
              <PromoteBatchVideoGenerator posts={posts} />
              <CopyCatPanel
                apiKey={apiKey}
                ctaKeyword={(ctaText || "GO").trim().split(/\s+/)[0].toUpperCase()}
                niche="digital nomad / living abroad"
                audience="Americans curious about moving abroad or earning remotely"
                offer="The Nomadic Wealth Academy"
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default PartnershipPortal;
